import { io } from 'socket.io-client'
import { API_URL } from '@/services/api.service'
import { logger } from '@/utils/logger'

/**
 * Socket Service - Handles Socket.io client communication
 */
class SocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.eventListeners = new Map() // event -> Set of callbacks
    this.connectionPromise = null
    this.cachedSocketUrl = null // Cache socket URL to prevent multiple API_URL() calls
    this.config = {
      options: {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxHttpBufferSize: 1e6 // 1MB
      }
    }
  }

  async getSocketUrl() {
    if (this.cachedSocketUrl) {
      return this.cachedSocketUrl
    }

    const apiUrl = await API_URL()
    this.cachedSocketUrl = apiUrl.replace('/v1', '')
    return this.cachedSocketUrl
  }

  /**
   * Connect to Socket.io server
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  async connect(token) {
    if (this.isConnected || this.isConnecting) {
      return this.connectionPromise
    }

    this.isConnecting = true

    const socketUrl = await this.getSocketUrl()

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.socket = io(socketUrl, {
          ...this.config.options,
          auth: {
            token
          }
        })

        this.socket.on('connect', () => {
          this.isConnected = true
          this.isConnecting = false
          this.reconnectAttempts = 0
          resolve()
        })

        this.socket.on('connect_error', (error) => {
          this.isConnecting = false

          // Handle specific websocket errors with retry
          if (error.message.includes('websocket error') && this.reconnectAttempts === 0) {
            // Don't immediately reject, let socket.io handle retries
            setTimeout(() => {
              if (!this.isConnected && this.socket) {
                this.socket.connect()
              }
            }, 2000)
          } else if (this.reconnectAttempts === 0) {
            this.isConnected = false
            reject(new Error(`Failed to connect: ${error.message}`))
          }
        })

        this.socket.on('disconnect', (reason) => {
          this.isConnected = false
          this.emit('socket:disconnect', { reason })

          if (reason === 'io server disconnect') {
            setTimeout(() => {
              if (!this.isConnected) {
                this.reconnect(token)
              }
            }, 1000)
          }
        })

        this.socket.on('reconnect', (attempt) => {
          this.isConnected = true
          this.reconnectAttempts = 0
          this.emit('socket:reconnect', { attempt })
        })

        this.socket.on('reconnect_attempt', (attempt) => {
          this.reconnectAttempts = attempt
          this.emit('socket:reconnect_attempt', { attempt })
        })

        this.socket.on('reconnect_failed', () => {
          this.emit('socket:reconnect_failed')
        })
      } catch (error) {
        this.isConnecting = false
        reject(error instanceof Error ? error : new Error(String(error)))
      }
    })

    return this.connectionPromise
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }

    this.isConnected = false
    this.isConnecting = false
    this.connectionPromise = null
    this.reconnectAttempts = 0
    this.eventListeners.clear()
    this.cachedSocketUrl = null
  }

  /**
   * Manually trigger reconnection
   * @param {string} token - JWT authentication token
   */
  async reconnect(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('socket:max_reconnect_attempts')
      return
    }

    this.disconnect()
    await new Promise((resolve) => setTimeout(resolve, 1000 * (this.reconnectAttempts + 1)))

    try {
      await this.connect(token)
    } catch (error) {
      this.reconnectAttempts++
      logger.error('Socket reconnection failed:', error.message)
    }
  }

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {Object} data - Event data
   * @param {Function} callback - Optional callback
   */
  emit(event, data, callback) {
    if (!this.isConnected || !this.socket) {
      return false
    }

    try {
      if (callback) {
        this.socket.emit(event, data, callback)
      } else {
        this.socket.emit(event, data)
      }
      return true
    } catch (error) {
      logger.error('Socket emit failed:', error.message)
      return false
    }
  }

  /**
   * Listen for an event from the server
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.socket) {
      return () => {}
    }

    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event).add(callback)

    this.socket.on(event, callback)

    return () => {
      if (this.socket) {
        this.socket.off(event, callback)
      }

      if (this.eventListeners.has(event)) {
        this.eventListeners.get(event).delete(callback)

        if (this.eventListeners.get(event).size === 0) {
          this.eventListeners.delete(event)
        }
      }
    }
  }

  /**
   * Listen for an event once
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  once(event, callback) {
    if (!this.socket) {
      return
    }

    this.socket.once(event, callback)
  }

  /**
   * Remove all listeners for an event
   * @param {string} event - Event name
   */
  off(event) {
    if (this.socket) {
      this.socket.off(event)
    }

    if (this.eventListeners.has(event)) {
      this.eventListeners.delete(event)
    }
  }

  /**
   * Check connection status
   * @returns {boolean} True if connected
   */
  isSocketConnected() {
    return this.isConnected && this.socket?.connected
  }

  /**
   * Get current socket ID
   * @returns {string|null} Socket ID or null
   */
  getSocketId() {
    return this.socket?.id || null
  }

  /**
   * Get connection statistics
   * @returns {Object} Connection statistics
   */
  getStats() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      socketId: this.getSocketId(),
      transport: this.socket?.io?.engine?.transport?.name,
      activeListeners: this.eventListeners.size,
      uptime: this.socket?.connected ? Date.now() - this.socket.connected : 0
    }
  }

  /**
   * Send ping to server to check connection
   */
  ping() {
    if (this.isConnected && this.socket) {
      const startTime = Date.now()
      this.socket.emit('ping', () => {
        const latency = Date.now() - startTime
        this.emit('socket:pong', { latency })
      })
    }
  }

  cleanup() {
    // Unregister all listeners before clearing
    for (const [event, callbacks] of this.eventListeners) {
      for (const callback of callbacks) {
        if (this.socket) {
          this.socket.off(event, callback)
        }
      }
    }
    this.eventListeners.clear()
  }
}

// Export singleton instance
const socketService = new SocketService()
export default socketService
